import { Component, DestroyRef, Input, NgZone, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { ActiveElement, ChartConfiguration } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { finalize } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ManagedServicesSharedDataService } from '../../../services/managed-services-shared-data.service';
import { ThemeService } from 'src/app/core/services/theme.service';
import { environment } from 'src/environments/environment';
import { MSPDashboardV2Component } from '../msp-dashboard-v2.component';

@Component({
  selector: 'app-dashboard-thirty-days-chart',
  templateUrl: './dashboard-thirty-days-chart.component.html',
  styleUrls: ['./dashboard-thirty-days-chart.component.scss'],
  standalone: false,
})
export class DashboardThirtyDaysChartComponent extends MSPDashboardV2Component implements OnChanges {
  @Input() dashboard: string;
  @Input() boards: string[] = [];

  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;

  isFetching = false;
  errorFetching = false;
  tickets30Days: { date: string; opened: number; closed: number }[] = [];

  lineChartData: ChartConfiguration<'line'>['data'] = { labels: [], datasets: [] };
  lineChartOptions: ChartConfiguration<'line'>['options'] = {};

  constructor(
    private http: HttpClient,
    private router: Router,
    private ngZone: NgZone,
    private destroyRef: DestroyRef,
    private managedServicesSharedDataService: ManagedServicesSharedDataService,
    private themeService: ThemeService,
  ) {
    super();
    this.themeService.themeSubject$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((theme) => this.onThemeChanged(theme));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dashboard']?.currentValue) {
      this.fetch();
    }
  }

  filterByChartLabel(elements: ActiveElement[]) {
    if (!elements || elements.length === 0) return;

    let index = 0;
    if (elements.length === 2) index = 1;

    const statusIndex = elements[index].datasetIndex;
    const dataIndex = elements[index].index;
    const statusType: 'open' | 'closed' =
      this.lineChartData.datasets[statusIndex].label === 'Opened tickets' ? 'open' : 'closed';
    const labelDate = this.lineChartData.labels[dataIndex] as string;
    const value = this.lineChartData.datasets[statusIndex].data[dataIndex] as number;

    if (value === 0) return;

    this.managedServicesSharedDataService.resetFilters();

    const startDate = new Date(`${labelDate}T05:00:00.000Z`);
    const endDate = new Date(startDate);
    endDate.setUTCDate(endDate.getUTCDate() + 1);
    endDate.setUTCMilliseconds(endDate.getUTCMilliseconds() - 1);

    if (statusType === 'open') {
      this.managedServicesSharedDataService.filters.createdAfter = startDate.toISOString();
      this.managedServicesSharedDataService.filters.createdBefore = endDate.toISOString();
    } else {
      this.managedServicesSharedDataService.filters.closedAfter = startDate.toISOString();
      this.managedServicesSharedDataService.filters.closedBefore = endDate.toISOString();
    }
    this.navigate(null);
  }

  retry() {
    this.fetch();
  }

  private onThemeChanged(theme: 'light' | 'dark' | 'auto') {
    const isLight = theme === 'light';
    this.lineChartOptions = {
      ...this.lineChartOptions,
      plugins: {
        ...this.lineChartOptions?.plugins,
        title: {
          ...this.lineChartOptions?.plugins?.title,
          color: isLight ? '#555555' : '#F1F1F1',
        },
        legend: {
          position: 'top',
          align: 'end',
          labels: { color: isLight ? '#555555' : '#F1F1F1' },
        },
      },
      scales: {
        x: {
          ticks: {
            autoSkip: false,
            maxRotation: 90,
            minRotation: 45,
            color: isLight ? '#555555' : '#F1F1F1',
          },
        },
        y: { beginAtZero: true, min: 0, ticks: { stepSize: 1, precision: 0 } },
      },
    };
    this.chart?.update();
  }

  private fetch() {
    const isDark = this.themeService.isInDarkMode();
    this.isFetching = true;
    this.isFetchingKpis.emit(true);
    this.errorFetching = false;

    this.http
      .get<{ date: string; opened: number; closed: number }[]>(
        `${environment.restUrl}/v1/msp/dashboards/${this.dashboard}/30-days`,
      )
      .pipe(
        finalize(() => {
          this.isFetching = false;
          this.isFetchingKpis.emit(false);
        }),
      )
      .subscribe({
        next: (thirtyDays) => {
          this.tickets30Days = thirtyDays;
          this.lineChartData = {
            labels: thirtyDays.map((d) => d.date),
            datasets: [
              {
                label: 'Opened tickets',
                data: thirtyDays.map((d) => d.opened),
                fill: true,
                backgroundColor: 'rgba(0, 193, 0, 0.2)',
                borderColor: '#00c100',
                pointBackgroundColor: '#00c100',
                pointHoverBorderColor: '#00c100',
                tension: 0.1,
              },
              {
                label: 'Closed tickets',
                data: thirtyDays.map((d) => d.closed),
                fill: true,
                backgroundColor: 'rgba(33, 179, 255, 0.2)',
                borderColor: '#21b3ff',
                pointBackgroundColor: '#21b3ff',
                pointHoverBorderColor: '#21b3ff',
                tension: 0.1,
              },
            ],
          };
          this.lineChartOptions = {
            responsive: true,
            maintainAspectRatio: false,
            onClick: (event, elements) => {
              this.ngZone.run(() => this.filterByChartLabel(elements));
            },
            onHover: (event: any, activeElements: any[]) => {
              const chart = event.native.target;
              if (activeElements.length > 0) {
                const datasetIndex = activeElements[0].datasetIndex;
                const index = activeElements[0].index;
                const value = this.lineChartData.datasets[datasetIndex].data[index];
                chart.style.cursor = Number(value) > 0 ? 'pointer' : 'default';
              } else {
                chart.style.cursor = 'default';
              }
            },
            plugins: {
              title: {
                display: true,
                text: 'Opened vs. Closed Tickets - Trailing 30 Days',
                align: 'start',
                font: { size: 16 },
                color: isDark ? '#F1F1F1' : '#555555',
              },
              legend: {
                position: 'top',
                align: 'end',
                labels: { color: isDark ? '#F1F1F1' : '#555555' },
              },
            },
            scales: {
              x: {
                ticks: {
                  autoSkip: false,
                  maxRotation: 90,
                  minRotation: 45,
                  color: isDark ? '#F1F1F1' : '#555555',
                },
              },
              y: { beginAtZero: true, min: 0, ticks: { stepSize: 1, precision: 0 } },
            },
          };
        },
        error: () => (this.errorFetching = true),
      });
  }

  private navigate(statusType: 'open' | 'closed' | null) {
    this.managedServicesSharedDataService.filters.boardNames = this.boards;
    this.managedServicesSharedDataService.filters.relationship = 'parent';
    this.managedServicesSharedDataService.filters.statusesGroup = statusType ? [statusType] : [];
    this.router.navigateByUrl(`/secured/managed-services/tickets?refresh=${Date.now()}`);
  }
}
